import type {
    AgentSkill,
    AgentSkillRevision,
} from "@common/schemas/domains/agentSkills/agent-skill.schema";
import { Tabs } from "@design-system/Tabs/Tabs";
import type { AgentSkillPanelTab } from "./agentSkillChat";
import { AgentSkillChatTab } from "./AgentSkillChatTab";
import { AgentSkillDetailsTab } from "./AgentSkillDetailsTab";
import type { AgentSkillFormValues } from "./agentSkillForm";
import { AgentSkillVersionsTab } from "./AgentSkillVersionsTab";
import type { useAgentSkillLifecycle } from "./useAgentSkillLifecycle";
import styles from "./AgentSkills.module.scss";

export type AgentSkillMetadataPatch = Partial<
    Pick<AgentSkillFormValues, "category" | "loadMode">
>;

const TABS: Array<{ value: AgentSkillPanelTab; label: string }> = [
    { value: "chat", label: "Chat" },
    { value: "details", label: "Details" },
    { value: "versions", label: "Versions" },
];

interface Props {
    skill: AgentSkill;
    tab: AgentSkillPanelTab;
    onTabChange: (tab: AgentSkillPanelTab) => void;
    canManage: boolean;
    /** Unsaved text locks the metadata pickers; a pick would replace it. */
    hasUnsavedChanges: boolean;
    pendingMetadata: AgentSkillMetadataPatch | null;
    clientName: string | null;
    restore: ReturnType<typeof useAgentSkillLifecycle>["restore"];
    onUseDraft: (body: string) => void;
    onMetadataChange: (patch: AgentSkillMetadataPatch) => void;
    /** Loads a version into the editor; absent in read mode. */
    onViewRevision?: (revision: AgentSkillRevision) => void;
    onMoved: (skillId: string) => void;
}

export const AgentSkillSidePanel = ({
    skill,
    tab,
    onTabChange,
    canManage,
    hasUnsavedChanges,
    pendingMetadata,
    clientName,
    restore,
    onUseDraft,
    onMetadataChange,
    onViewRevision,
    onMoved,
}: Props) => (
    <Tabs.Root
        value={tab}
        onValueChange={(value) => {
            const next = TABS.find((candidate) => candidate.value === value);
            if (next) {
                onTabChange(next.value);
            }
        }}
        className={styles.paneTabs}
    >
        <Tabs.List className={styles.paneTabList} hasIndicator={false}>
            {TABS.map(({ value, label }) => (
                <Tabs.Tab key={value} value={value} className={styles.paneTab}>
                    {label}
                </Tabs.Tab>
            ))}
        </Tabs.List>
        <Tabs.Panel value="chat" className={styles.paneChatPanel} keepMounted>
            <AgentSkillChatTab
                key={skill.id}
                skill={skill}
                canManage={canManage}
                isMetadataSaving={pendingMetadata !== null}
                onUseDraft={onUseDraft}
                onMoved={onMoved}
            />
        </Tabs.Panel>
        <Tabs.Panel value="details" className={styles.panePanel} keepMounted>
            <AgentSkillDetailsTab
                skill={skill}
                clientName={clientName}
                canEditMetadata={canManage}
                hasUnsavedChanges={hasUnsavedChanges}
                pendingMetadata={pendingMetadata}
                onMetadataChange={onMetadataChange}
            />
        </Tabs.Panel>
        <Tabs.Panel value="versions" className={styles.panePanel}>
            <AgentSkillVersionsTab
                skill={skill}
                canManage={canManage}
                restore={restore}
                onViewRevision={onViewRevision}
                onMoved={onMoved}
            />
        </Tabs.Panel>
    </Tabs.Root>
);
